import { useQuery } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type AlertCategory =
  | "STOCK_BAJO"
  | "AGOTADO"
  | "VENCIMIENTO_PROXIMO"
  | "LOTE_VENCIDO"
  | "CXP_VENCIDA"
  | "SIN_MOVIMIENTO"
  | "MARGEN_BAJO"
  | "PRECIO_BAJO_COSTO";

export type AlertSeverity = "INFO" | "WARNING" | "CRITICAL";

export interface AlertItem {
  category: AlertCategory;
  severity: AlertSeverity;
  title: string;
  message: string;
  entityType: "Product" | "ProductBatch" | "SupplierPayable";
  entityId: string;
  entityLabel: string;
  value?: number | string | null;
  referenceDate?: string | null;
}

export interface AlertsCenterReport {
  generatedAt: string;
  counts: Record<AlertCategory, number>;
  totalCount: number;
  items: AlertItem[];
}

export const ALERT_CATEGORY_LABEL: Record<AlertCategory, string> = {
  STOCK_BAJO: "Stock bajo",
  AGOTADO: "Agotado",
  VENCIMIENTO_PROXIMO: "Próximo a vencer",
  LOTE_VENCIDO: "Lote vencido",
  CXP_VENCIDA: "Cuenta por pagar vencida",
  SIN_MOVIMIENTO: "Sin movimiento",
  MARGEN_BAJO: "Margen bajo",
  PRECIO_BAJO_COSTO: "Precio bajo costo",
};

/** Rutas de tienda a las que un link "ver" de cada categoría de alerta debería llevar. */
export const ALERT_CATEGORY_ROUTE: Record<AlertCategory, string> = {
  STOCK_BAJO: "/store/products",
  AGOTADO: "/store/products",
  VENCIMIENTO_PROXIMO: "/store/products",
  LOTE_VENCIDO: "/store/products",
  CXP_VENCIDA: "/store/payables",
  SIN_MOVIMIENTO: "/store/products",
  MARGEN_BAJO: "/store/products",
  PRECIO_BAJO_COSTO: "/store/products",
};

export interface AlertsCenterFilters {
  expirationDaysAhead?: number;
  noMovementDays?: number;
  lowMarginThreshold?: number;
  categories?: AlertCategory[];
}

/** Centro de Alertas unificado — ver alerts.service.ts en el backend. Se calcula en vivo en cada consulta. */
export function useAlertsCenter(filters: AlertsCenterFilters = {}) {
  return useQuery({
    queryKey: ["store", "alerts", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<AlertsCenterReport>>("/alerts", {
        params: { ...filters, categories: filters.categories?.join(",") },
      });
      return data.data;
    },
    refetchInterval: 5 * 60 * 1000,
  });
}
